import _ from "lodash";
import {useCallback, useEffect, useState} from "react";
import {useAtomValue} from "jotai";
import {useResourceManagement} from "../hooks/resourceHooks";
import {swallowException} from "../utils/appUtils";
import {isResourceSucceeded} from "../utils/resourceUtils";
import {getInvitationsForUser} from "../services/invitationService";
import {UserInvitationEx} from "../types/domain/userInvitation";
import {UserNotificationItem} from "../types/userNotificationItem";
import {authenticatedUserLoadable} from "../store/authenticatedUserStore";
import {OrderStatus} from "../types/domain/orderStatus";
import {getOrdersForUser} from "../services/orderService";
import {Order} from "../types/domain/order";

/**
 * Fetches the list of User Notification items, including Invitations and Orders
 */
export const useAuthUserNotificationItems = () => {
    const [notificationItems, setNotificationItems] = useState<UserNotificationItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const authUserState = useAtomValue(authenticatedUserLoadable);
    const user = authUserState.state === "hasData" && authUserState.data !== undefined ? authUserState.data : undefined;

    const invitationsManagement = useResourceManagement({getInvitationsForUser});
    const {getInvitationsForUser: getInvitations, resource: invitations} = invitationsManagement;

    const ordersManagement = useResourceManagement({getOrdersForUser});
    const {getOrdersForUser: getOrders, resource: orders} = ordersManagement;

    const refresh = useCallback(() => {
        if (user) {
            getInvitations(user.id).catch(swallowException);
            getOrders(user.id, {state: [OrderStatus.AWAITING_CONFIRMATION]}).catch(swallowException);
        }
    }, [getInvitations, getOrders, user]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (user && isResourceSucceeded(invitations) && isResourceSucceeded(orders)) {
            const invitationItems = isResourceSucceeded(invitations)
                ? invitations.data.map(invitationToNotificationItem)
                : [];
            const orderItems = isResourceSucceeded(orders) ? orders.data.map(orderToNotificationItem) : [];
            const items: UserNotificationItem[] = [...invitationItems, ...orderItems];
            setNotificationItems(_.sortBy(items, "date", "desc"));
            setIsLoading(false);
        }
    }, [invitations, orders, setNotificationItems, user, setIsLoading]);

    return {notificationItems, isLoading, refresh};
};

const invitationToNotificationItem = (invitation: UserInvitationEx): UserNotificationItem => ({
    notificationType: "Invitation",
    date: new Date(invitation.createdAt),
    invitation,
});

const orderToNotificationItem = (order: Order): UserNotificationItem => ({
    notificationType: "Order",
    date: order.startDate ? new Date(order.startDate) : new Date(),
    order,
});
